import { action, observable, reaction, makeObservable } from 'mobx';
import OpusMediaRecorder from 'opus-media-recorder';

import { t } from '@gem/modules/i18n';
import dateService from '@gem/services/date-service';
import {
  isMediaRecorderSupportedWavFormat,
  convertBufferToBase64,
  convertBufferToPeaks,
} from '@gem/utils';

import Tasks from 'APP/Tasks';
import { AudioSourceType } from 'APP/constants/app';
import { Root } from 'APP/store';

import { VOICE_BITRATE, VOICE_DURATION_MIN } from './Voice.constants';
import { EncoderConfig, IVoiceData, WorkerMessageType } from './Voice.types';

interface IStartResult {
  result: boolean;
  error?: string;
}

const workerOptions = {
  encoderWorkerFactory(): Worker {
    return new Worker(new URL('opus-media-recorder/encoderWorker.js', import.meta.url), {
      type: 'module',
    });
  },
};

const recorderMimeType = 'audio/wav';
const fileMimeType = 'audio/mp3';
const downloadFileExt = '.mp3';

export class Voice {
  @observable isStarted = false;
  @observable isRecording = false;
  @observable isPreviewMode = false;

  @observable blob: Blob | null = null;
  @observable src: string | null = null;
  @observable peaks: Uint8Array | null = null;

  root: Root;
  recorder: MediaRecorder | typeof OpusMediaRecorder | null = null;
  stream: MediaStream;

  changeActiveGroupReactionDispose: (() => void) | null = null;
  removeAudioSource: (() => void) | null = null;
  stopPromiseResolve: (() => void) | null = null;

  onStartHandler: () => void;
  onDataAvailableHandler: (e: BlobEvent) => void;
  onStopHandler: () => Promise<void>;

  constructor(root: Root) {
    makeObservable(this);
    this.root = root;

    this.onStartHandler = this.onStart.bind(this);
    this.onDataAvailableHandler = this.onDataAvailable.bind(this);
    this.onStopHandler = this.onStop.bind(this);
  }

  @action
  async start(): Promise<IStartResult> {
    const hasAudioPermission = await Tasks.permissions.hasMicrophonePermission();
    if (!hasAudioPermission) {
      return { result: false };
    }

    this.isStarted = true;
    this.stream = await (navigator as Navigator).mediaDevices.getUserMedia({ audio: true });

    if (isMediaRecorderSupportedWavFormat()) {
      this.recorder = new MediaRecorder(this.stream, { mimeType: recorderMimeType });
    } else {
      this.recorder = new OpusMediaRecorder(
        this.stream,
        { mimeType: recorderMimeType },
        workerOptions
      );
    }

    this.recorder.addEventListener('start', this.onStartHandler);
    this.recorder.addEventListener('dataavailable', this.onDataAvailableHandler);
    this.recorder.addEventListener('stop', this.onStopHandler);

    try {
      await this.recorder.start();
      return { result: true };
    } catch ({ name }) {
      this.isStarted = false;

      if (name === 'NotAllowedError') {
        this.reset();
        return { result: false, error: t('calls_no_microphone_permissions') };
      }

      if (name === 'NotReadableError') {
        this.reset();
        return { result: false, error: t('calls_no_microphone_connected') };
      }

      return { result: false };
    }
  }

  @action
  stop(): Promise<void> {
    return new Promise((resolve): void => {
      if (this.recorder.state !== 'inactive') {
        this.stopPromiseResolve = resolve;
        this.recorder.stop();
        this.stopStreamTracks();
        return;
      }

      this.stopStreamTracks();
      resolve();
    });
  }

  @action
  setPeaks(peaks: Uint8Array): void {
    this.peaks = peaks;
  }

  @action
  setPreview(isPreview: boolean): void {
    this.isPreviewMode = isPreview;
  }

  @action
  reset(): void {
    this.recorder?.removeEventListener('start', this.onStartHandler);
    this.recorder?.removeEventListener('dataavailable', this.onDataAvailableHandler);
    this.recorder?.removeEventListener('stop', this.onStopHandler);

    if (this.isRecording && this.recorder.state !== 'inactive') {
      this.recorder.stop();
    }

    this.isPreviewMode = false;
    this.blob = null;
    this.src = null;
    this.recorder = null;
    this.changeActiveGroupReactionDispose?.();
    this.changeActiveGroupReactionDispose = null;
    this.clearAudioSource();
    this.isRecording = false;
    this.stopStreamTracks();
    this.removeAudioSource = null;
  }

  get duration(): number {
    return this.recorder?.context.currentTime || 0;
  }

  stopStreamTracks(): void {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
    }
  }

  async audioBufferToFloat32Array(
    buffer: ArrayBuffer
  ): Promise<{ float32Array: Float32Array; audioBuffer: AudioBuffer }> {
    const audioBuffer = await this.recorder.context.decodeAudioData(buffer.slice(0));
    const channelData = audioBuffer.getChannelData(0); // we'll only use the first channel
    return { float32Array: new Float32Array(channelData), audioBuffer };
  }

  getVoiceData(): Promise<IVoiceData> {
    return new Promise(async (resolve): Promise<void> => {
      const fileName = dateService.now().toISOString() + downloadFileExt;
      const arrayBuffer = (await this.getArrayBuffer()) as ArrayBuffer;
      const { float32Array, audioBuffer } = await this.audioBufferToFloat32Array(arrayBuffer);

      const voiceWorker = new Worker(new URL('./Voice.worker', import.meta.url));
      const encoderConfig: EncoderConfig = {
        sampleRate: audioBuffer.sampleRate,
        bitRate: VOICE_BITRATE,
      };

      voiceWorker.postMessage({ type: WorkerMessageType.CreateEncoder, data: encoderConfig });
      voiceWorker.postMessage({ type: WorkerMessageType.Encode, data: float32Array });
      voiceWorker.postMessage({ type: WorkerMessageType.FinalBuffer });

      voiceWorker.onmessage = async (event: MessageEvent): Promise<void> => {
        if (event.data.type === WorkerMessageType.FinalBuffer) {
          const mp3blob = new Blob(event.data.data, { type: fileMimeType });
          const mp3File = new File([mp3blob], fileName, { type: fileMimeType });

          const peaks = await convertBufferToPeaks(arrayBuffer);
          const base64Peaks = convertBufferToBase64(peaks);

          voiceWorker.postMessage({ type: WorkerMessageType.ClearBuffer });

          resolve({
            file: mp3File,
            histogram: base64Peaks,
            duration: this.duration * 1000,
          });
        }
      };
    });
  }

  onDataAvailable(e: BlobEvent): void {
    if (this.recorder.state === 'inactive') {
      this.blob = new Blob([e.data], { type: recorderMimeType });
      this.src = URL.createObjectURL(this.blob);
    }
  }

  onStart(): void {
    this.isStarted = false;
    this.isRecording = true;
    this.isPreviewMode = false;
    this.changeActiveGroupReactionDispose = reaction(
      () => this.root.GroupsStore.activeGroup,
      () => this.reset()
    );

    this.removeAudioSource = Tasks.app.audioSource.setCurrentSource(
      AudioSourceType.VoiceRecording,
      this.stop
    );
  }

  async onStop(): Promise<void> {
    if (this.duration < VOICE_DURATION_MIN) {
      return this.reset();
    }

    if (!this.isRecording) {
      return;
    }

    this.isRecording = false;
    this.clearAudioSource();

    if (this.stopPromiseResolve) {
      this.stopPromiseResolve();
      this.stopPromiseResolve = null;
    }
  }

  clearAudioSource(): void {
    if (this.removeAudioSource) {
      this.removeAudioSource();
      this.removeAudioSource = null;
    }
  }

  // All browsers support
  async getArrayBuffer(): Promise<string | ArrayBuffer | null> {
    return new Promise((resolve) => {
      const fileReader = new FileReader();

      fileReader.onloadend = function (): void {
        resolve(fileReader.result);
      };

      if (!this.blob) {
        throw new Error('Can not get array buffer. No blob');
      }

      fileReader.readAsArrayBuffer(this.blob);
    });
  }

  async getAudioHistogram(): Promise<string> {
    const arrayBuffer = await this.getArrayBuffer();
    const peaks = await convertBufferToPeaks(arrayBuffer as ArrayBuffer);
    return convertBufferToBase64(peaks);
  }
}
